import { Box, Grid, makeStyles, Paper, Radio, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { StepTitle } from '../../components/stepTitle';
import { LeverageFormStore } from '../../stores/loanEconomics/leverageFormStore';
import { QuoteTypeStore } from '../../stores/quoteTypeStore';
import { sum } from '../../utils/utils';
import { LeverageExceptionAlert } from '../summary/components/leverageExceptionAlert';
import { LeverageSubmarketAlert } from '../summary/components/leverageSubmarketAlert';
import { ErrorsAlert } from '../preflight/preflightStep';
import { PortalMessageFor3MLoans } from '../summary/components/portalMessageFor3MLoans';

const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  activeLabel: {
    color: theme.palette.primary.main,
  },
  inactiveLabel: {
    color: theme.palette.text.secondary,
  },
}));

export interface LeverageCardProps {
  store: LeverageFormStore;
  disabled: boolean;
  instaQuote?: boolean;
  showEmptyValues?: boolean;
  LeverageMatrixCard?: React.FC<LeverageMatrixCardProps>;
  quoteTypeStore: QuoteTypeStore;
  manualQuoteSubTitle?: string;
  onPercentageChange?: () => void;
}
export interface SuggestedLeverageCardProps {
  suggestedLoanQuote: Record<string, any>;
  quoteTypeStore: QuoteTypeStore;
}
export interface ITableMatrixData {
  COLUMNS_2: Record<string, any>;
  DATA_2: Record<string, any>;
  ROW_VALUE_KEYS: string[];
}
export interface LeverageMatrixCardProps {
  store: QuoteTypeStore;
  data?: ITableMatrixData;
}

interface LeverageStepProps {
  store: QuoteTypeStore;
  allowInstaQuote?: boolean;
  showFinancedIR?: boolean;
  LeverageCard: React.FC<LeverageCardProps>;
  LeverageMatrixCard?: React.FC<LeverageMatrixCardProps>;
  FinancedInterestReserve?: React.FC<LeverageMatrixCardProps>;
  SuggestedLeverageCard?: React.FC<SuggestedLeverageCardProps>;
  isManualQuote?: boolean;
  onPercentageChange?: () => void;
}

const MANUAL_QUOTE_SUBTITLE = 'Manual underwriter review is needed, and quotes are usually provided in about 2 business days.';

export const LeverageStep = observer(
  ({
    store,
    allowInstaQuote,
    LeverageMatrixCard,
    LeverageCard,
    FinancedInterestReserve,
    SuggestedLeverageCard,
    isManualQuote,
    onPercentageChange = () => { }
  }: LeverageStepProps) => {
    const classes = useStyles();
    const { loanEconomicsStore } = store;
    const { enableManualQuote, userInputOnLeverage, instaQuoteRanges } = loanEconomicsStore;
    const isManualLeverageRequired = store.isManualLeverageRequired(loanEconomicsStore.instaLeverageQuoteFormStore?.calculatedFields?.totalLoanAmount || 0) || allowInstaQuote === false || loanEconomicsStore.instaQuoteRanges?.eligible === false;

    const showFinancedInterestReserve = !!loanEconomicsStore.showFinancedInterestReserve && !!FinancedInterestReserve;
    const isTotalLoanAmountGreaterThan3M = loanEconomicsStore.isTotalLoanAmountGreaterThan3M;
    const totalPurchasePrice = sum(
      store.propertiesStore.propertyFormStores.map(
        formStore => formStore.form.fields.purchasePrice.value || 0
      )
    );
    const submarketLevel = loanEconomicsStore.instaLeverageQuoteFormStore?.form.fields.subMarketLevel.value ?? 0;

    useEffect(() => {
      loanEconomicsStore.checkFinancedInterestReserveRules(totalPurchasePrice);
    }, [totalPurchasePrice])

    useEffect(() => {
      const keepManualQuoteSelected = store.globalStore.isInternalPortal && isManualQuote;

      if (!keepManualQuoteSelected && !isManualLeverageRequired && enableManualQuote && !userInputOnLeverage) {
        loanEconomicsStore.setEnableManualQuote(false);
      }
    }, [instaQuoteRanges?.maxLoanToCost]);

    const propertyFormHash = JSON.stringify([
      store.propertiesStore.propertyFormStores.map(form =>
        form.getFormValues()
      ),
    ]);

    useEffect(() => {
      loanEconomicsStore.checkPropertyFormHash(propertyFormHash);
    }, [propertyFormHash])

    const hash = JSON.stringify([
      store.borrowersStore.borrowerFormStores.map(form => form.getFormValues()),
      store.propertiesStore.propertyFormStores.map(form =>
        form.getFormValues()
      ),
    ]);

    useEffect(() => {
      if (totalPurchasePrice > 0) {
        loanEconomicsStore.fecthInstaQuoteRanges();
      }
    }, [totalPurchasePrice, hash]);

    const do_enableManualQuote = () => {
      loanEconomicsStore.setUserInputOnLeverage(true);
      loanEconomicsStore.setEnableManualQuote(true);
    };

    const do_enableInstaQuote = () => {
      if (!isManualLeverageRequired) {
        loanEconomicsStore.setUserInputOnLeverage(true);
        loanEconomicsStore.setEnableManualQuote(false);
      }
    };

    useEffect(() => {
      if (isManualLeverageRequired && !enableManualQuote) {
        loanEconomicsStore.setEnableManualQuote(true);
      }
    }, [isManualLeverageRequired, loanEconomicsStore.instaLeverageQuoteFormStore?.calculatedFields?.totalLoanAmount]);

    useEffect(() => {
      if (store.globalStore.isInternalPortal && store.loanId) {
        store.getSuggestedQuote();
      }
    }, [store.loanId]);

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StepTitle
              title={isManualQuote ? <>Manual Exception Terms</> : <>Leverage</>}
              subtitle={
                <>
                  Below in green is the maximum leverage we can offer based on the
                  borrower's FICO score and experience. You can adjust this using the (+)
                  and (-) buttons or request leverage if desired.
                </>
              }
            />
          </Grid>
          {!!showFinancedInterestReserve && (
            <Grid item xs={12}>
              <FinancedInterestReserve store={store} />
            </Grid>
          )}
          {store.globalStore.isInternalPortal && isManualQuote && SuggestedLeverageCard && (
            <Grid item xs={12}>
              <Box style={{ flexGrow: 1, marginLeft: 42, opacity: .7 }}>
                {store.suggestedLoanQuote && Object.keys(store.suggestedLoanQuote).length > 0
                  ? <SuggestedLeverageCard suggestedLoanQuote={store.suggestedLoanQuote} quoteTypeStore={store} />
                  : <Paper
                      style={{
                        border: '2px solid',
                        borderRadius: '16px !important',
                        color: '#4CAF50',
                        opacity: .7,
                        padding: 10
                      }}
                    >
                      No Suggested Insta-Quote was provided for this loan.
                    </Paper>
                }
              </Box>
            </Grid>
          )}
          {isManualQuote &&
            <Grid item xs={12}>
              <Box display="flex" flexDirection={'row'} alignItems={'center'}>
                <Box>
                  <Radio
                    checked={enableManualQuote}
                    onChange={() => do_enableManualQuote()}
                    value=""
                    name=""
                    color='primary'
                  />
                </Box>
                <Box style={{ cursor: 'pointer', flexGrow: 1 }} onClick={() => {
                  do_enableManualQuote()
                }}>
                  <LeverageCard
                    store={loanEconomicsStore.manualExceptionQuoteFormStore}
                    disabled={!enableManualQuote}
                    quoteTypeStore={store}
                    manualQuoteSubTitle={isManualQuote ? MANUAL_QUOTE_SUBTITLE : null}
                    onPercentageChange={onPercentageChange}
                  />
                </Box>
              </Box>
            </Grid>
          }
          {!isManualQuote &&
            <>
              <Grid item xs={12}>
                <Box display="flex" flexDirection={'row'} alignItems={'center'}>
                  <Box>
                    <Radio
                      checked={!enableManualQuote}
                      onChange={() => do_enableInstaQuote()}
                      value=""
                      name=""
                      color='primary'
                      disabled={isManualLeverageRequired}
                    />
                  </Box>
                  <Box style={isManualLeverageRequired ? { flexGrow: 1 } : { cursor: 'pointer', flexGrow: 1 }} onClick={() => {
                    do_enableInstaQuote()
                  }}>
                    <LeverageCard
                      store={loanEconomicsStore.instaLeverageQuoteFormStore}
                      disabled={enableManualQuote}
                      instaQuote
                      showEmptyValues={isManualLeverageRequired}
                      LeverageMatrixCard={LeverageMatrixCard}
                      quoteTypeStore={store}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" flexDirection={'row'} alignItems={'center'}>
                  <Box>
                    <Radio
                      checked={enableManualQuote}
                      onChange={() => do_enableManualQuote()}
                      value=""
                      name=""
                      color='primary'
                    />
                  </Box>
                  <Box style={{ cursor: 'pointer', flexGrow: 1 }} onClick={() => {
                    do_enableManualQuote()
                  }}>
                    <LeverageCard
                      store={loanEconomicsStore.manualExceptionQuoteFormStore}
                      disabled={!enableManualQuote}
                      quoteTypeStore={store}
                    />
                  </Box>
                </Box>
              </Grid>
            </>
          }

          {
            isTotalLoanAmountGreaterThan3M ? (
              <Grid item xs={12}>
                <Box ml={5}>
                  <PortalMessageFor3MLoans />
                </Box>
              </Grid>
            ) : null
          }
          {
            (!isTotalLoanAmountGreaterThan3M && enableManualQuote && !isManualQuote) ? (
              <Grid item xs={12}>
                <Box ml={5}>
                  <LeverageExceptionAlert />
                </Box>
              </Grid>
            ) : null
          }
          {
            (submarketLevel > 0) && (
              <Grid item xs={12}>
                <Box ml={5}>
                  <LeverageSubmarketAlert submarket={submarketLevel} />
                </Box>
              </Grid>
            )
          }
          {
            store.stepErrors?.length > 0 ? (
              <Grid item xs={12}>
                <Box ml={5}>
                  <ErrorsAlert errors={store.stepErrors} />
                </Box>
              </Grid>
            ) : null
          }
        </Grid>
      </>
    );
  }
);
